import { FC, ReactNode, useEffect, useRef } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import './FloatingActionBar.scss';

type FloatingActionBarProps = {
  count: number;
  countLabel: string;
  onClose: () => void;
  children: ReactNode;
  // When true, the close button is disabled and shows `disabledTooltip` on hover.
  disabled?: boolean;
  disabledTooltip?: string;
};

const FloatingActionBar: FC<FloatingActionBarProps> = ({ count, countLabel, onClose, children, disabled, disabledTooltip }) => {
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar || count === 0) return;

    const content = document.querySelector('.scrollable-content');
    if (!content) return;

    const updatePosition = () => {
      const rect = content.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      bar.style.left = `${center}px`;
      bar.style.transform = 'translateX(-50%)';
    };

    requestAnimationFrame(updatePosition);
    const observer = new ResizeObserver(updatePosition);
    observer.observe(content);
    return () => observer.disconnect();
  }, [count]);

  if (count === 0) return null;

  return (
    <div className="floating-action-bar" ref={barRef}>
      <button
        className={`floating-action-bar__action${disabled ? ' floating-action-bar__action--disabled' : ''}`}
        onClick={disabled ? undefined : onClose}
        aria-disabled={disabled}
        title={disabled ? disabledTooltip : undefined}
        style={{ padding: '6px 8px' }}
      >
        <CloseIcon style={{ fontSize: 16 }} />
      </button>
      <span className="floating-action-bar__count">
        {count} {countLabel}
      </span>
      <div className="floating-action-bar__divider" />
      {children}
    </div>
  );
};

export default FloatingActionBar;

export const FloatingActionBarButton: FC<{
  icon: ReactNode;
  label: string;
  onClick: () => void;
  // When true, the button is non-interactive and shows `disabledTooltip` on hover.
  // Uses a class (not the native disabled attribute) so the hover tooltip still fires.
  disabled?: boolean;
  disabledTooltip?: string;
  loaderShow?: boolean;
}> = ({ icon, label, onClick, disabled, disabledTooltip, loaderShow }) => {
  const isBlocked = disabled || loaderShow;
  return (
    <button
      className={`floating-action-bar__action${isBlocked ? ' floating-action-bar__action--disabled' : ''}${loaderShow ? ' floating-action-bar__action--loading' : ''}`}
      onClick={isBlocked ? undefined : onClick}
      aria-disabled={isBlocked}
      title={disabled ? disabledTooltip : undefined}
    >
      <span className="floating-action-bar__action-content">
        {icon}
        {label}
      </span>
      <span className="floating-action-bar__loader">
        <span className="bounce1" />
        <span className="bounce2" />
        <span className="bounce3" />
      </span>
    </button>
  );
};

export const FloatingActionBarDivider: FC = () => (
  <div className="floating-action-bar__divider" />
);
